import { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import {
    DataGrid,
    GridColDef,
    GridRowSelectionModel,
} from "@mui/x-data-grid";
import { Refresh } from "@mui/icons-material";
import { httpsCallable } from "firebase/functions";
import { useSnackbar } from "notistack";
import CenteredCircularProgress from "../../../core/LoadingScreen";
import firebaseApps from "../../../config/firebase";
import { AVAILABLE_ROLES } from "../../../core/auth/roles";

interface AdminUser {
    uid: string;
    email: string | null;
    displayName: string | null;
    firstLogin: string | null;
    lastLogin: string | null;
    roles: string[];
    fileCount: number;
}

// Built lazily inside the component rather than at module scope: evaluating
// these on import forces Firebase SDK init as a side effect of importing.
const getCallables = () => ({
    listUsersWithStats: httpsCallable<unknown, { users: AdminUser[] }>(
        firebaseApps.functions,
        "listUsersWithStats"
    ),
    setUserRoles: httpsCallable<
        { uids: string[]; roles: string[] },
        { updated: Array<{ uid: string; roles: string[] }> }
    >(firebaseApps.functions, "setUserRoles"),
});

const toDate = (value: string | null): Date | null =>
    value ? new Date(value) : null;

const columns: GridColDef<AdminUser>[] = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "displayName", headerName: "Name", flex: 1, minWidth: 150 },
    {
        field: "roles",
        headerName: "Roles",
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
            <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center", height: "100%" }}
            >
                {(params.row.roles ?? []).map((role) => (
                    <Chip key={role} label={role} size="small" color="primary" />
                ))}
            </Stack>
        ),
    },
    { field: "fileCount", headerName: "Files", type: "number", width: 90 },
    {
        field: "firstLogin",
        headerName: "First login",
        type: "dateTime",
        minWidth: 180,
        valueGetter: (value) => toDate(value as string | null),
    },
    {
        field: "lastLogin",
        headerName: "Last login",
        type: "dateTime",
        minWidth: 180,
        valueGetter: (value) => toDate(value as string | null),
    },
];

function Users() {
    const { enqueueSnackbar } = useSnackbar();
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [rows, setRows] = useState<AdminUser[]>([]);
    const [selection, setSelection] = useState<GridRowSelectionModel>([]);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [rolesToApply, setRolesToApply] = useState<string[]>([]);
    const [applying, setApplying] = useState<boolean>(false);

    const loadUsers = useCallback(async () => {
        setDataLoaded(false);
        try {
            const result = await getCallables().listUsersWithStats();
            setRows(result.data.users ?? []);
        } catch (error: any) {
            enqueueSnackbar(error?.message ?? "Failed to load users", {
                variant: "error",
            });
        } finally {
            setDataLoaded(true);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const affectedCount = selection.length;

    const openDialog = () => {
        const firstSelected = rows.find((row) => row.uid === selection[0]);
        setRolesToApply(firstSelected ? [...firstSelected.roles] : []);
        setDialogOpen(true);
    };

    const toggleRole = (role: string) => {
        setRolesToApply((current) =>
            current.includes(role)
                ? current.filter((r) => r !== role)
                : [...current, role]
        );
    };

    const applyRoles = async () => {
        setApplying(true);
        try {
            await getCallables().setUserRoles({
                uids: selection.map((id) => String(id)),
                roles: rolesToApply,
            });
            enqueueSnackbar(
                `Roles updated for ${affectedCount} user(s)`,
                { variant: "success" }
            );
            setDialogOpen(false);
            setSelection([]);
            await loadUsers();
            // The caller's own roles may have changed; refresh their token so
            // the nav/route admin gate reflects it without a re-login.
            await firebaseApps.auth.currentUser?.getIdToken(true);
        } catch (error: any) {
            enqueueSnackbar(error?.message ?? "Failed to update roles", {
                variant: "error",
            });
        } finally {
            setApplying(false);
        }
    };

    return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Grid container sx={{ m: 2 }} maxWidth="lg" spacing={2}>
                <Grid item xs={12}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ alignItems: "center" }}
                    >
                        <Typography variant="h3" sx={{ flexGrow: 1 }}>
                            Users
                        </Typography>
                        <Button
                            variant="contained"
                            disabled={affectedCount === 0}
                            onClick={openDialog}
                        >
                            Assign roles
                            {affectedCount > 0 ? ` (${affectedCount})` : ""}
                        </Button>
                        <IconButton
                            onClick={loadUsers}
                            aria-label="Refresh users"
                        >
                            <Refresh />
                        </IconButton>
                    </Stack>
                </Grid>
                <Grid item xs={12}>
                    {dataLoaded ? (
                        <DataGrid
                            autoHeight
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => row.uid}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selection}
                            onRowSelectionModelChange={(model) =>
                                setSelection(model)
                            }
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        page: 0,
                                        pageSize: 25,
                                    },
                                },
                            }}
                            pageSizeOptions={[10, 25, 50]}
                        />
                    ) : (
                        <CenteredCircularProgress />
                    )}
                </Grid>
            </Grid>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Assign roles</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This will set the selected roles for{" "}
                        <strong>{affectedCount}</strong> user
                        {affectedCount === 1 ? "" : "s"}. Existing roles on
                        those users will be replaced.
                    </DialogContentText>
                    <FormGroup>
                        {AVAILABLE_ROLES.map((role) => (
                            <FormControlLabel
                                key={role}
                                control={
                                    <Checkbox
                                        checked={rolesToApply.includes(role)}
                                        onChange={() => toggleRole(role)}
                                    />
                                }
                                label={role}
                            />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        disabled={applying}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={applyRoles}
                        disabled={applying || affectedCount === 0}
                    >
                        {applying
                            ? "Applying…"
                            : `Apply to ${affectedCount} user${
                                  affectedCount === 1 ? "" : "s"
                              }`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Users;
